import React, { useState } from 'react'
import { useRouteMatch } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { GoogleLogin, GoogleLogout } from 'react-google-login'
import { get } from 'lodash'
import Container from '@material-ui/core/Container'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import { useSnackbar } from 'notistack'
import { PageHeader } from 'components/page-header'
import { usePortal } from 'lib/portal'
import { useAuth } from 'lib/auth'
import SettingsBox from 'components/coach-settings-box'
import { ReactComponent as GoogleCalendarIcon } from 'images/icons/google-calendar-colored.svg'
import { ReactComponent as CalendarSyncIcon } from 'images/icons/calendar-sync.svg'
import useStyles from './integrations.styles'
import { API_ROOT, GOOGLE_CLIENT_ID as googleClientId } from 'env'
import SubscribeAction, { SubscribeMapper } from './subscribe-menu'
import {
  GRANT_RESOURCE_ACCESS,
  REVOKE_RESOURCE_ACCESS,
  WEBCAL_QUERY,
} from './queries'
import LinearProgress from '@material-ui/core/LinearProgress'

const IntegrationsPage = () => {
  const auth = useAuth()
  const googleCalendarConnected = get(auth, 'user.googleCalendarConnected')

  const { enqueueSnackbar } = useSnackbar()
  const { path } = useRouteMatch()

  const [target] = usePortal()
  const styles = useStyles()
  const [isConnectedCalendar, setIsConnectedCalendar] = useState(
    !!googleCalendarConnected,
  )

  const [grantResourceAccess] = useMutation(GRANT_RESOURCE_ACCESS, {
    onCompleted: data => {
      if (data.grantResourceAccess.success) {
        setIsConnectedCalendar(true)
      } else {
        enqueueSnackbar(data.grantResourceAccess.message, {
          variant: 'error',
        })
      }
    },
  })
  const [revokeResourceAccess] = useMutation(REVOKE_RESOURCE_ACCESS, {
    onCompleted: data => {
      if (data.revokeResourceAccess.success) {
        setIsConnectedCalendar(false)
      } else {
        enqueueSnackbar(data.revokeResourceAccess.message, {
          variant: 'error',
        })
      }
    },
  })

  const [selectedCalendar, setSelectedCalendar] = useState('default')
  const { data, loading } = useQuery(WEBCAL_QUERY)

  const calUrl = new URL(
    `/coach/${data?.currentUser?.externalId}/webcal_feed`,
    API_ROOT,
  )

  const crumbs = [
    {
      label: 'Home',
      to: path
        .split('/')
        .slice(0, 2)
        .join('/'),
    },
    {
      label: 'Settings',
      to: path
        .split('/')
        .slice(0, 3)
        .join('/'),
    },
  ]

  const googleResourceName = 'google'

  const responseGoogleSuccess = response => {
    grantResourceAccess({
      variables: {
        input: {
          key: response.code,
          resourceName: googleResourceName,
        },
      },
    })
  }

  const responseGoogleFailure = response => {
    enqueueSnackbar('Google Login Failure', {
      variant: 'error',
    })
  }

  const logoutGoogleSuccess = response => {
    revokeResourceAccess({
      variables: {
        input: {
          resourceName: googleResourceName,
        },
      },
    })
    setIsConnectedCalendar(false)
  }

  const logoutGoogleFailure = response => {
    enqueueSnackbar('Google Logout Failure', {
      variant: 'error',
    })
  }

  const sharedProps = {
    accessType: 'offline',
    cookiePolicy: 'single_host_origin',
    scope: 'https://www.googleapis.com/auth/calendar.readonly',
  }

  const actionConnect = (
    <GoogleLogin
      clientId={googleClientId}
      render={renderProps => (
        <Button
          variant="contained"
          color="primary"
          className={styles.action}
          onClick={renderProps.onClick}
        >
          Connect
        </Button>
      )}
      onSuccess={responseGoogleSuccess}
      onFailure={responseGoogleFailure}
      responseType="code"
      {...sharedProps}
    />
  )

  const actionDisconnect = (
    <GoogleLogout
      clientId={googleClientId}
      render={renderProps => (
        <Button
          color="primary"
          className={styles.action}
          onClick={renderProps.onClick}
        >
          Disconnect
        </Button>
      )}
      onLogoutSuccess={logoutGoogleSuccess}
      onFailure={logoutGoogleFailure}
      {...sharedProps}
    />
  )

  const subscribeIcon = (
    <div className={styles.iconBordered}>
      <CalendarSyncIcon />
    </div>
  )

  return (
    <>
      <PageHeader title="Integrations" crumbs={crumbs} actions={target} />
      <Container className={styles.root}>
        <Typography variant="h6" gutterBottom>
          Calendar sync
        </Typography>
        <Grid container spacing={2}>
          <Grid item lg={7} md={8} xs={12}>
            <SettingsBox
              icon={<GoogleCalendarIcon />}
              title="Set Availability"
              subtitle="Gmail, Google Workspace"
              action={isConnectedCalendar ? actionDisconnect : actionConnect}
            />
          </Grid>
          {loading && <LinearProgress color="secondary" />}
          {data?.currentUser?.externalId && (
            <Grid item lg={7} md={8} xs={12}>
              <SettingsBox
                icon={subscribeIcon}
                title={SubscribeMapper[selectedCalendar]}
                subtitle="Import your PGA events into your personal calendar."
                action={
                  <SubscribeAction
                    calUrl={calUrl}
                    selectedCalendar={selectedCalendar}
                    setSelectedCalendar={setSelectedCalendar}
                  />
                }
              />
            </Grid>
          )}
        </Grid>
      </Container>
    </>
  )
}

export default IntegrationsPage
